// The routes. Paths are real URLs (/invoices/{id}) that work on reload, in
// a new tab and with Back; Firebase Hosting serves app.html for each.
import { useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';
import { AppDataProvider, useAppData } from '@/app/AppData';
import { Shell } from '@/app/Shell';
import { pagePattern, trackPage } from '@/data/analytics';
import { ClientDetailPage } from '@/pages/ClientDetail';
import { ClientFormPage } from '@/pages/ClientForm';
import { ClientsPage } from '@/pages/Clients';
import { DashboardPage } from '@/pages/Dashboard';
import { InvoiceDetailPage } from '@/pages/InvoiceDetail';
import { InvoiceEditorPage } from '@/pages/InvoiceEditor';
import { InvoicesPage } from '@/pages/Invoices';
import { PaywallPage } from '@/pages/Paywall';
import { SettingsPage } from '@/pages/Settings';
import { SetupPage } from '@/pages/Setup';
import { MaintenancePage, NotFoundPage, SignInFailedPage, StartingPage } from '@/pages/System';
import { WelcomePage } from '@/pages/Welcome';
import { ToastProvider } from '@/ui/Toasts';

/** Reports each page shown as a screen view, by its pattern ("/invoices/:id"). */
function PageView() {
  const { pathname } = useLocation();
  const pattern = pagePattern(pathname);
  useEffect(() => trackPage(pattern), [pattern]);
  return null;
}

/**
 * Holds every page until startup is done, then sends people where they
 * belong: a new guest to the welcome page, an account without a business to
 * setup, everyone else on to the page they asked for.
 */
function Gate() {
  const { phase, user, business } = useAppData();
  const { pathname, search } = useLocation();
  if (phase === 'starting') return <StartingPage />;
  if (phase === 'maintenance') return <MaintenancePage />;
  if (phase === 'signInFailed') return <SignInFailedPage />;
  if (business === undefined) return <StartingPage />;
  const open = ['/welcome', '/sign-in', '/setup'];
  if (business === null && !open.includes(pathname)) return <Navigate to={user?.isGuest ? '/welcome' : '/setup'} replace />;
  // Setup runs again only to add another business (/setup?add=1).
  const addingBusiness = pathname === '/setup' && new URLSearchParams(search).get('add') === '1';
  if (business && (pathname === '/welcome' || (pathname === '/setup' && !addingBusiness))) return <Navigate to="/home" replace />;
  return (
    <>
      <PageView />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <Gate />,
    children: [
      { path: '/', element: <Navigate to="/home" replace /> },
      { path: '/welcome', element: <WelcomePage mode="welcome" /> },
      { path: '/sign-in', element: <WelcomePage mode="backup" /> },
      { path: '/setup', element: <SetupPage /> },
      { path: '/pro', element: <PaywallPage /> },
      {
        element: <Shell />,
        children: [
          { path: '/home', element: <DashboardPage /> },
          { path: '/invoices', element: <InvoicesPage /> },
          { path: '/invoices/new', element: <InvoiceEditorPage /> },
          { path: '/invoices/:id', element: <InvoiceDetailPage /> },
          { path: '/invoices/:id/edit', element: <InvoiceEditorPage /> },
          { path: '/clients', element: <ClientsPage /> },
          { path: '/clients/new', element: <ClientFormPage /> },
          { path: '/clients/:id', element: <ClientDetailPage /> },
          { path: '/clients/:id/edit', element: <ClientFormPage /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/settings/:section', element: <SettingsPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <ToastProvider>
      <AppDataProvider>
        <RouterProvider router={router} />
      </AppDataProvider>
    </ToastProvider>
  );
}
